/**
 * /preview/3d/:artifactId — the interactive 3D model viewer page (Tier-21 deliverable,
 * 2026-08-01 Miles directive). Deep-link target of the chat "Open interactive 3D preview"
 * action button. Resolves the artifact through prest0nResolve3dPreview (owner-gated,
 * admin-only artifact docs; the scene HTML comes back inline so no storage URL, CORS
 * grant, or frame-src exception is needed) and renders it in a sandboxed iframe with an
 * OPAQUE origin — the scene's own three.js orbit/rotate/zoom controls work, but its
 * scripts can never touch the app origin.
 *
 * It only draws: resolving the model, full screen, the download and back are
 * src/hooks/filePreview/useThreeDPreviewPage.ts (PV5-8:C part 1, 2026-10-13).
 */
import React from 'react';
import styled from 'styled-components';
import { injectRuntimeGuard } from './sceneRuntimeGuard';
import { useThreeDPreviewPage } from '../../hooks/filePreview/useThreeDPreviewPage';

const Page = styled.div`
  position: fixed; inset: 0; background: #0a0f1a; color: #e8edf6;
  display: flex; flex-direction: column;
`;

const Bar = styled.div`
  flex: 0 0 auto; display: flex; align-items: center; gap: 12px;
  padding: 10px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const BarTitle = styled.div`
  flex: 1; min-width: 0; font-size: 14px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const BarButton = styled.button`
  background: rgba(255, 255, 255, 0.07); color: #dbe4f2;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px; padding: 6px 12px; font-size: 12px; cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.14); }
`;

const SceneFrame = styled.iframe`
  flex: 1; border: 0; width: 100%; background: #fff;
`;

const Center = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  color: #93a1b8; font-size: 14px; text-align: center; padding: 24px;
`;

const Preview3DPage: React.FC = () => {
  const { scene, failure, frameWrapRef, fullscreen, downloadScene, back } = useThreeDPreviewPage();
  const error = failure === 'not-found'
    ? 'This 3D model is unavailable — it may have been removed or belong to another account.'
    : failure === 'auth-required'
      ? 'Sign in to view this 3D model.'
      : failure
        ? 'The 3D model could not be loaded right now. Please try again.'
        : null;

  return (
    <Page>
      <Bar>
        <BarButton onClick={back} aria-label="back">← Back</BarButton>
        <BarTitle>{scene?.title || 'Interactive 3D model'}</BarTitle>
        {scene && <BarButton onClick={fullscreen}>Fullscreen</BarButton>}
        {scene && <BarButton onClick={downloadScene}>Download</BarButton>}
      </Bar>
      {error && <Center>{error}</Center>}
      {!error && !scene && <Center>Loading 3D model…</Center>}
      {!error && scene && (
        <div ref={frameWrapRef} style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <SceneFrame
            title={scene.title || 'Interactive 3D model'}
            sandbox="allow-scripts allow-pointer-lock"
            allow="pointer-lock *; fullscreen *"
            // Guard injected (2026-08-09 black-preview report) — boot
            // failures surface a plain-language overlay instead of black.
            srcDoc={injectRuntimeGuard(scene.html)}
          />
        </div>
      )}
    </Page>
  );
};

export default Preview3DPage;
